import React, { useState, useEffect, useContext, useMemo } from 'react';
import moment from 'moment';
import { Button } from 'reactstrap';
import ReactTable from 'react-table';
import _ from 'lodash';
import { WithWizard } from 'react-albus';
import classnames from 'classnames';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { AppContext } from '../../context/index';
import { IMap } from '../../globals/interfaces';
import { formatDatetime, formatMawb, formatPercent, notify } from '../../utils';
import styled from 'styled-components';
import ExpandingImage from '../ExpandingImage';
import { useSearchAwb } from '../searchAwb/useSearchAwb';
import ActionIcon from './ActionIcon';
import { borderTopLeftRadius, borderTopRightRadius } from 'html2canvas/dist/types/css/property-descriptors/border-radius';

const getSquareImage = (url: string) => {
    if (url && url.length > 0) {
        return url.replace('.png', '-sq.png');
    }
    return '';
};

interface RenderImageProps {
    value: string;
    square?: boolean;
    imageWidth?: string;
    imageHeight?: string;
    borderRadius?: string;
}

const RenderImage = ({
    value,
    square,
    imageWidth,
    imageHeight,
    borderRadius
}: RenderImageProps) => {
    const useValue = square ? getSquareImage(value) : value;

    return (
        <ExpandingImage
            src={useValue}
        />
    );
};

interface AdditionalProps<T> {
    exclude?: boolean;
    id?: string;
    function?: (current: T) => any;
    selectable?: boolean;
    showCondition?: (current: T) => boolean;
    valueOther?: string;
    datetime?: boolean;
    date?: boolean;
    utc?: boolean;
    shortDate?: boolean;
    monthYear?: boolean;
    monthDay?: boolean;
    time?: boolean;
    money?: boolean;
    decimal?: boolean;
    email?: boolean;
    boolean?: boolean;
    labelTrue?: string;
    labelFalse?: string;
    button?: boolean;
    color?: string;
    disabled?: (current: T) => boolean;
    concat?: boolean;
    values?: Array<string>;
    operator?: string;
    nested?: boolean;
    image?: boolean;
    subvalue?: string;
    square?: boolean;
    imageWidth?: string;
    imageHeight?: string;
    borderRadius?: string;
    importPaymentMethod?: boolean;
    s_mawb?: boolean;
    percent?: boolean;
    percentDecimal?: boolean;
    breakdownUnder?: boolean;
    breakdownOver?: boolean;
    deepNested?: boolean;
    keys?: Array<string>;
    nestedArray?: boolean;
    substring?: boolean;
    count?: number;
    hardValue?: boolean;
    multiple?: boolean;
    number?: boolean;
    customWidth?: number;
    smallWidth?: boolean;
    mediumWidth?: boolean;
    largeWidth?: boolean;
    medLargeWidth?: boolean;
    extraLargeWidth?: boolean;
    maxWidth?: boolean;
    sortMethod?: (a: any, b: any) => number;
    sortType?: any;
    password?: boolean;
    filter?: boolean;
    dev?: boolean;
    displayColor?: boolean;
    bgColor?: string;
    bgColorCondition?: (item: any) => boolean;
    center?: boolean;
    byron?: boolean;
    delete?: boolean;
}

interface Props<T> {
    data: Array<any>;
    mapping: Array<
        | ({ name: string; value: keyof T; icon: false } & AdditionalProps<T>)
        | ({ name: string; value: string; icon: true } & AdditionalProps<T>)
        | ({ name: string; value: keyof T; icon?: undefined } & AdditionalProps<T>) // Handling cases where icon is omitted
    >;
    handleClick?: (item: any) => void;
    numRows?: number;
    locked?: boolean;
    index?: boolean;
    enableClick?: boolean;
    customHeight?: string;
    defaultFiltered?: Array<any>;
    reactTableRef?: any;
    className?: string;
    selectable?: boolean;
    selectedIds?: any;
    disableGlobalFilter?: any;
    wizardNext?: boolean;
    onClickNext?: any;
    defaultSorting?: any;
    noPagination?: boolean;
    customPagination?: boolean;
    externalSelectedRowIndex?: number;
}

export default function VirtualTable<T>({
    data,
    mapping,
    handleClick,
    numRows,
    locked,
    index = true,
    enableClick,
    customHeight,
    defaultFiltered,
    reactTableRef,
    className,
    selectedIds,
    selectable,
    disableGlobalFilter,
    wizardNext,
    onClickNext,
    defaultSorting,
    noPagination,
    externalSelectedRowIndex
}: Props<T>) {
    /* 
        export const mapping = [
            {
                name: 'ID',
                value: 'id',
                datetime: true,
                utc: true,
                email: true,
                money: true,
                icon: true,
                nested: true,
                subvalue: '',
                function: (item) => removeStationCustomer(item.stationCustomerRecordId)
            },
        ]
    */
    const dataWithIndex = data && data.map((item, index) => {
        return { ...item, index };
    });
    const { user } = useContext(AppContext);
    const { handleSearchAwb } = useSearchAwb();
    const [selectedRow, setSelectedRow] = useState('');
    const { isDev } = user;
    const [waitNext, setWaitNext] = useState(false);

    const handleNext = (item: any) => {
        handleClick && handleClick(item);
        setWaitNext(true);
    };

    const isSelected = (d: any) => {
        if (!d) return false;
        const rowId = _.get(d, ['id'], '');
        const isSelectedByIndex = selectedRow === d.index;
        const isSelectedById = selectedIds
            ? selectedIds.has(String(rowId))
            : false;
        const selected =
            isSelectedByIndex || isSelectedById;
        return selected;
    }

    const dataTableColumns = useMemo(() => {
        const columns = [];

        columns.push({
            Header: '#',
            id: 'row',
            maxWidth: 50,
            filterable: false,
            Cell: (row: any) => {
                return (
                    <div style={{ textAlign: 'right' }}>{row.index + 1}</div>
                );
            },
        });

        const defaultFilters: Array<{ id: string, value: string }> = [];

        for (let i = 0; i < mapping.length; i++) {
            const current = mapping[i];
            const checkDev = !current.dev || (current.dev && isDev);
            const checkByron = !current.byron || (current.byron && user.s_email === 'BYRON@CHOICE.AERO');

            if (current.exclude || !checkDev || !checkByron) continue;

            const column: any = {
                id: current.id ? current.id : `id${i}`,
                Header: current.name,
                accessor: current.icon
                    ? (d: any) => {
                        const selected = isSelected(d);

                        // Use these variables to set the className
                        const textColor = selected
                            ? 'text-white'
                            : 'text-success';
                        const className = `${String(current.value)} ${textColor}`;

                        const handleClickIcon = () => {

                            current.function && current.function(d);
                        };

                        const conditionallyUpdateIcon = () => {
                            return (
                                (current.selectable && selected(d.id)) ||
                                (current.icon &&
                                    current.selectable &&
                                    !selected(d.id)) ||
                                (current.icon &&
                                    current.showCondition &&
                                    current.showCondition(d)) ||
                                (current.icon && !current.showCondition)
                            );
                        };

                        const iconClass = conditionallyUpdateIcon()
                            ? className
                            : current.valueOther;

                        return (
                            <i
                                style={{ width: current.customWidth || 50 }}
                                className={iconClass}
                                onClick={handleClickIcon}
                            />
                        );
                    }
                    : (d: any) =>
                        current.datetime || current.date ? (
                            d[current.value] &&
                                moment(d[current.value]).isValid() &&
                                d[current.value] !== '' && // Exclude empty strings
                                d[current.value] !== null ? (
                                current.utc ? (
                                    current.datetime ? (
                                        moment
                                            .utc(d[current.value])
                                            .format('MM/DD/YYYY HH:mm')
                                    ) : (
                                        moment
                                            .utc(d[current.value])
                                            .format('MM/DD/YYYY')
                                    )
                                ) : current.datetime ? (
                                    moment(d[current.value]).format(
                                        'MM/DD/YYYY HH:mm'
                                    )
                                ) : (
                                    moment(d[current.value]).format(
                                        'MM/DD/YYYY'
                                    )
                                )
                            ) : (
                                ''
                            )
                        ) : current.shortDate ? (
                            current.utc ? (
                                moment
                                    .utc(d[current.value])
                                    .format('MM-DD HH:mm')
                            ) : (
                                moment(d[current.value]).format('MM-DD HH:mm')
                            )
                        ) : current.monthYear && d[current.value] ? (
                            current.utc ? (
                                moment
                                    .utc(d[current.value])
                                    .format('MM/YY HH:mm')
                            ) : (
                                moment(d[current.value]).format('MM/YY HH:mm')
                            )
                        ) : current.time && d[current.value] ? (
                            current.utc ? (
                                moment
                                    .utc(d[current.value])
                                    .format('HH:mm')
                            ) : (
                                moment(d[current.value]).format('HH:mm')
                            )
                        ) : current.monthDay && d[current.value] ? (
                            current.utc ? (
                                moment
                                    .utc(d[current.value])
                                    .format('MM/DD HH:mm')
                            ) : (
                                moment(d[current.value]).format('MM/DD HH:mm')
                            )
                        ) : current.money ? (
                            `$${formatCost(d[current.value])}`
                        ) : current.decimal ? (
                            _.get(d, `[${String(current.value)}]`, null)?.toFixed(1) ?? ''
                        ) : current.email ? (
                            d[current.value] &&
                            `${d[current.value]
                                .toUpperCase()
                                .replace('@CHOICE.AERO', '')}`
                        ) : current.boolean && current.showCondition ? (
                            current.showCondition(d) ? (
                                'YES'
                            ) : (
                                'NO'
                            )
                        ) : current.boolean && !current.showCondition ? (
                            d[current.value] ? (
                                current.labelTrue || 'YES'
                            ) : (
                                current.labelFalse || 'NO'
                            )
                        ) : current.button ? (
                            <Button
                                color={current.color || 'primary'}
                                onClick={() =>
                                    current.function && current.function(d)
                                }
                                disabled={
                                    (current.disabled &&
                                        current.disabled(d)) ||
                                    false
                                }
                            >
                                {current.value}
                            </Button>
                        ) : current.concat ? (
                            <span>
                                {resolveConcatValues(
                                    d,
                                    current.values,
                                    current.operator
                                )}
                            </span>
                        ) : current.nested ? (
                            current.image ? (
                                <RenderImage
                                    value={_.get(
                                        d,
                                        `[${String(current.value)}][${current.subvalue}]`,
                                        ''
                                    )}
                                    square={current.square}
                                    imageWidth={current.imageWidth}
                                    imageHeight={current.imageHeight}
                                    borderRadius={current.borderRadius}
                                />
                            ) : current.datetime ? (
                                'working'
                            ) : (
                                _.get(
                                    d,
                                    `[${String(current.value)}][${current.subvalue}]`,
                                    ''
                                )
                            )
                        ) : current.deepNested ? (
                            resolveDeepNested(d, current.keys, current.datetime)
                        ) : current.nestedArray ? (
                            <span>
                                {resolveNestedArray(
                                    d,
                                    String(current.value),
                                    current.subvalue,
                                    current.operator
                                )}
                            </span>
                        ) : current.image ? (
                            <RenderImage
                                value={d[current.value]}
                                square={current.square}
                                imageWidth={current.imageWidth}
                                imageHeight={current.imageHeight}
                                borderRadius={current.borderRadius}
                            />
                        ) : current.importPaymentMethod ? (
                            !['OVERRIDE', 'CHARGE'].includes(
                                d[current.value]
                            ) ? (
                                'PAYMENT'
                            ) : (
                                d[current.value]
                            )
                        ) : current.s_mawb ? (
                            formatMawb(d[current.value])
                        ) : current.percent ? (
                            formatPercent(
                                d[current.value],
                                current.percentDecimal
                            )
                        ) : current.breakdownUnder ? (
                            Math.abs(
                                Math.max(
                                    d['i_actual_piece_count'] -
                                    d['rackPieces'],
                                    0
                                )
                            )
                        ) : current.breakdownOver ? (
                            Math.abs(
                                Math.min(
                                    d['i_actual_piece_count'] -
                                    d['rackPieces'],
                                    0
                                )
                            )
                        ) : 
                        current.delete ? (
                            <ActionIcon 
                                type={'delete'}
                                confirm
                                onClick={() => current.function(d)}
                                simulateLoading
                                confirmSimple
                            />
                        )
                        : current.substring ? (
                            d[current.value].substring(0, current.count)
                        ) : current.hardValue ? (
                            current.value
                        ) : current.displayColor ?
                                <ColorDisplay color={String(d[current.value])} /> : 
                            (current.bgColor && current.bgColorCondition && current.bgColorCondition(d) || current.bgColor && !current.bgColorCondition) ? 
                                <div style={{ backgroundColor: current.bgColor }}>{d[current.value]}</div> :
                                (
                                    d[current.value]
                                ), 
                columns: current.multiple ? [] : null,
                style: {
                    textAlign: `${current.money || current.percent || current.number || current.decimal
                        ? 'right'
                        : current.icon || current.button || current.center
                            ? 'center'
                            : 'left'
                        }`,
                    wordBreak: 'table-cell'
                },
                width:
                    (current.customWidth && current.customWidth) ||
                    ((current.icon || current.smallWidth) && 50) ||
                    (current.percent || current.displayColor && 65) ||
                    (current.mediumWidth && 75) ||
                    ((current.medLargeWidth || current.email) && 125) ||
                    (current.largeWidth && 250) ||
                    (current.extraLargeWidth && 350) ||
                    (current.maxWidth && 500) ||
                    ((current.date || current.delete) && 100) ||
                    (current.datetime && 160) ||
                    (current.s_mawb && 136) ||
                    (current.square && 35),
                filterable: disableGlobalFilter
                    ? false
                    : current.icon || current.button || current.image
                        ? false
                        : true,
                // sortMethod: current.money ? sortMoney : current.sortMethod && current.sortMethod,
                // sortType: current.sortType && current.sortType,
            };

            // Add the proper sort method based on column type
            if (current.money) {
                column.sortMethod = sortMoney;
            } else if (current.date || current.datetime) {
                column.sortMethod = sortDatetime;
            } else if (current.sortMethod) {
                column.sortMethod = current.sortMethod;
            }

            // Add sortType if provided
            if (current.sortType) {
                column.sortType = current.sortType;
            }

            // If the column is for passwords, customize the Cell function
            if (current.password) {
                // @ts-ignore
                column.Cell = (row: any) => {
                    return (
                        <div className={'text-center'}>
                            <CopyToClipboard text={row.original[current.value]}>
                                <CopyIcon
                                    className="fa-duotone fa-copy text-success"
                                    onClick={() => notify('Copied')}
                                />
                            </CopyToClipboard>
                        </div>
                    );
                };
            }

            columns.push(column);

            if (current.filter) {
                // @ts-ignore
                defaultFilters.push({ id: column.id, value: current.filter });
            }
        }

        const hasMawb = mapping.find((m) => m.s_mawb === true);

        if (hasMawb) {
            columns.push({
                Header: '',
                id: 'mawb-search',
                maxWidth: 50,
                filterable: false,
                Cell: (row: any) => {
                    const selected = isSelected(row);
                    return (
                        <i
                            className={`fad fa-search pl-2 ${selected ? 'text-white' : 'text-success'}`}
                            onClick={() =>
                                handleSearchAwb(
                                    null,
                                    _.get(row, 'original.s_mawb', null)
                                )
                            }
                        />
                    );
                },
            });
        }

        if (wizardNext) {
            columns.push({
                Header: '',
                id: 'row',
                maxWidth: 50,
                filterable: true,
                Cell: (row: any) => {
                    const selected = isSelected(row);

                    return (
                        <WithWizard
                            render={({
                                next,
                                previous,
                                step,
                                steps,
                                push,
                            }: any) => (
                                <i
                                    className={classnames(
                                        `fas fa-chevron-circle-right ${selected ? 'text-white' : 'text-success '}`,
                                        { 'custom-disabled': waitNext }
                                    )}
                                    onClick={() => {
                                        handleNext(row.original);
                                        setSelectedRow(row.index);
                                        setTimeout(() => {
                                            onClickNext(
                                                next,
                                                steps,
                                                step,
                                                push
                                            );
                                            setWaitNext(false);
                                        }, 1000);
                                    }}
                                ></i>
                            )}
                        ></WithWizard>
                    );
                },
            });
        }

        if (selectable) {
            columns.push({
                Header: '',
                id: 'row',
                maxWidth: 50,
                filterable: true,
                Cell: (row: any) => {
                    if (
                        row &&
                        row.original.id &&
                        selectedIds.has(String(row.original.id))
                    ) {
                        return <i className={'fas fa-check-circle'} />;
                    }
                    return <div></div>;
                },
            });
        }

        return { columns, defaultFilters };
    }, [mapping, index, selectedIds, waitNext, selectable, isSelected]);

    const filterCaseInsensitive = (filter: any, row: any) => {
        const id = filter.pivotId || filter.id;
        if (row[id] !== null && typeof row[id] === 'string') {
            return row[id] !== undefined
                ? String(row[id].toLowerCase()).includes(
                    filter.value.toLowerCase()
                )
                : true;
        } else {
            return String(row[filter.id]) === filter.value;
        }
    };

    const onRowClick = (
        state: any,
        rowInfo: any,
        column: any,
        instance: any
    ) => {
        const selected = isSelected(rowInfo);

        // Use these variables to set the background color
        const backgroundColor = selected ? '#6fb327' : null;
        const color = selected ? 'white' : 'black';

        return {
            onClick: (e: any) => {
                if (!e.target.className.includes('fa-search') && !e.target.className.includes('fa-ruler-combined') && !e.target.className.includes('fa-trash') && !e.target.className.includes('fa-down')) {
                    handleClick && handleClick(rowInfo.original);
                    setSelectedRow(rowInfo.index);
                }
                // console.log(state);
                // console.log('A Td Element was clicked!')
                // console.log('it produced this event:', e)
                // console.log(e.target.className);
                // console.log('It was in this column:', column)
                // console.log('It was in this row:', rowInfo) //rowInfo.original
                // console.log('It was in this table instance:', instance)
            },
            style: {
                backgroundColor,
                color
            },
        };
    };

    const formatCost = (cost: string | number) => {
        if (Number(cost) < 0) {
            const useCost = Math.abs(Number(cost));
            return `${useCost.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
        }
        const toFormat =
            cost && cost !== null && Number(cost) > 0 ? Number(cost) : 0;
        return `${toFormat.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
    };

    const resolveConcatValues = (
        d: any,
        values: Array<string>,
        operator: string
    ) => {
        let string = '';

        values && values.map((v) => (string += `${d[v]}${operator}`));

        if (string[string.length - 1] === operator) {
            string = string.substring(0, string.length - 1);
        }

        return string;
    };

    const resolveNestedArray = (
        d: any,
        value: string,
        subvalue: string,
        operator: string
    ) => {
        let string = '';
        const array = d[value] || [];
        array.map((a: any) => (string += `${a[subvalue]}${operator} `));
        string = string.substr(0, string.length - 2);
        return string;
    };

    const resolveDeepNested = (d: any, keys: Array<string>, datetime?: boolean) => {
        for (let i = 0; i < keys.length; i++) {
            d = d[keys[i]];
        }
        return datetime ? formatDatetime(d) : d;
    };

    useEffect(() => {
        // This checks if the prop is provided and is a number
        if (typeof externalSelectedRowIndex === 'number') {
            setSelectedRow(externalSelectedRowIndex.toString()); // Assumes selectedRow is expected to be a string, adjust if necessary
        }
    }, [externalSelectedRowIndex]);

    return (
        <ReactTable
            // key={JSON.stringify(Array.from(selectedIds))}
            data={dataWithIndex}
            // @ts-ignore
            columns={dataTableColumns.columns}
            defaultPageSize={numRows}
            showPageSizeOptions={locked ? false : true}
            showPagination={noPagination ? false : true}
            filterable={true}
            defaultFilterMethod={(filter, row) =>
                filterCaseInsensitive(filter, row)
            }
            getTrProps={onRowClick}
            className={`-striped -highlight ${className} ${enableClick && 'hover'}`}
            style={{
                height: `${customHeight && customHeight}`,
                borderTopLeftRadius: 10,
                borderTopRightRadius: 10,
                borderBottomLeftRadius: 10,
                borderBottomRightRadius: 10
            }}
            //@ts-ignore
            defaultFiltered={dataTableColumns.defaultFilters}
            defaultSorted={defaultSorting}
            ref={reactTableRef}
        />
    );
}

export const sortMoney = (a: string, b: string) => {
    // Remove $ sign and any commas, then convert to float
    const numA = parseFloat((a || '').replace(/[$,]/g, ''));
    const numB = parseFloat((b || '').replace(/[$,]/g, ''));

    // Handle cases where parsing might fail
    const valA = isNaN(numA) ? 0 : numA;
    const valB = isNaN(numB) ? 0 : numB;

    return valA - valB;
};

export const sortDatetime = (a: string, b: string) => {
    // Expected formats: with or without time
    const formats = ["MM/DD/YYYY HH:mm", "MM/DD/YYYY"];

    const dateA = moment(a, formats, true);
    const dateB = moment(b, formats, true);

    // Invalid dates are treated as 0 (epoch) or sorted last — your choice
    const timeA = dateA.isValid() ? dateA.valueOf() : Infinity;
    const timeB = dateB.isValid() ? dateB.valueOf() : Infinity;

    return timeA - timeB;
};

export const CopyIcon = styled.i`
    font-size: 22px;
    &:hover {
        cursor: pointer;
    }
`;

const ColorDisplay = styled.div<{ color: string }>`
    width: 40px;
    height: 40px;
    background-color: ${p => p.color || 'white'};
    border: 1px solid #000;
`;